import type {TFunction} from 'i18next';
import {
  RESERVATION_PAYMENT_TYPES,
  type CartPayment,
  type PaymentsCart,
} from '@chekin/core';
import {formatMoney, toNumber} from './money';

type PaymentKind = 'charge' | 'hold';

const HOLD_PAYMENT_TYPES: CartPayment['payment_type'][] = [
  'PRE_AUTH_PAYMENT',
  'RETENTION_PAYMENT',
];

function getPaymentKind(payment: CartPayment): PaymentKind {
  return HOLD_PAYMENT_TYPES.includes(payment.payment_type) ? 'hold' : 'charge';
}

type CartSplit = {
  charges: CartPayment[];
  holds: CartPayment[];
  holdTotal: number;
  preAuthTotal: number;
  paidOutside: number;
};

const REQUEST_HOLD_TYPES: CartPayment['obj_type'][] = [
  RESERVATION_PAYMENT_TYPES.upselling,
  RESERVATION_PAYMENT_TYPES.upsellingThirdParty,
];

function findDepositPayment(paymentsCart?: PaymentsCart) {
  return paymentsCart?.pay_now_payments?.find(
    payment => payment.obj_type === RESERVATION_PAYMENT_TYPES.securityDeposit,
  );
}

function getDepositFee(
  paymentsCart: PaymentsCart | undefined,
  paymentTypes: CartPayment['payment_type'][] = HOLD_PAYMENT_TYPES,
) {
  const deposit = findDepositPayment(paymentsCart);

  return deposit && paymentTypes.includes(deposit.payment_type)
    ? toNumber(deposit.commission_amount)
    : 0;
}

function isRetentionPayment(type?: CartPayment['payment_type']) {
  return type === 'RETENTION_PAYMENT';
}

function isHoldPayment(payment: CartPayment) {
  return getPaymentKind(payment) === 'hold';
}

type FeeSplitAmounts = {amount: string; amount_with_commission: string};

function getAmountOnCard(entry: FeeSplitAmounts, isHold: boolean) {
  return isHold ? entry.amount : entry.amount_with_commission;
}

function isRequestHold(objType?: CartPayment['obj_type']) {
  return Boolean(objType && REQUEST_HOLD_TYPES.includes(objType));
}

function sumBlockedAmounts(payments: CartPayment[]) {
  const total = payments.reduce((sum, payment) => sum + toNumber(payment.amount), 0);

  return Math.round(total * 100) / 100;
}

function splitCart(paymentsCart?: PaymentsCart): CartSplit {
  const payNow = paymentsCart?.pay_now_payments ?? [];
  const holds = payNow.filter(payment => getPaymentKind(payment) === 'hold');

  return {
    charges: payNow.filter(payment => getPaymentKind(payment) === 'charge'),
    holds,
    holdTotal: sumBlockedAmounts(
      holds.filter(payment => isRetentionPayment(payment.payment_type)),
    ),
    preAuthTotal: sumBlockedAmounts(
      holds.filter(payment => !isRetentionPayment(payment.payment_type)),
    ),
    paidOutside: toNumber(paymentsCart?.outside_paid_amount),
  };
}

function getHoldSummary(paymentsCart?: PaymentsCart) {
  const {holds, holdTotal, preAuthTotal} = splitCart(paymentsCart);

  return {
    heldTotal: holdTotal + preAuthTotal,
    isAuthorisation:
      holds.length > 0 && holds.every(hold => !isRetentionPayment(hold.payment_type)),
  };
}

const INFO_HINT_KEYS: Partial<Record<string, string>> = {
  [RESERVATION_PAYMENT_TYPES.touristTaxes]: 'what_is_this_hint_taxes',
  [RESERVATION_PAYMENT_TYPES.securityDeposit]: 'what_is_this_hint_deposit',
  [RESERVATION_PAYMENT_TYPES.damageProtection]: 'what_is_this_hint_protection',
};

function getInfoHint(t: TFunction, objType?: string) {
  const key = objType && INFO_HINT_KEYS[objType];

  return key ? t(key) : undefined;
}

function isProtectionPayment(objType?: string) {
  return (
    objType === RESERVATION_PAYMENT_TYPES.securityDeposit ||
    objType === RESERVATION_PAYMENT_TYPES.damageProtection
  );
}

function getHoldSubtitle(
  t: TFunction,
  payment: {objType?: CartPayment['obj_type']; type?: CartPayment['payment_type']},
) {
  if (isRequestHold(payment.objType)) return t('authorised_charged_only_if_approved');
  return isRetentionPayment(payment.type)
    ? t('pay_flow.held_not_charged')
    : t('pay_flow.authorised_not_charged');
}

function getCartItemEditLabel(objType?: string) {
  if (objType === RESERVATION_PAYMENT_TYPES.touristTaxes) return 'add_exemptions';
  return isProtectionPayment(objType) ? 'edit_protection' : 'edit_options';
}

type PayActionTotals = {
  chargeTotal: number;
  heldTotal: number;
  isAuthorisation: boolean;
  currencySymbol: string;
};

function getPayActionLabelKey({
  chargeTotal,
  heldTotal,
  isAuthorisation,
}: PayActionTotals) {
  if (chargeTotal > 0) {
    if (heldTotal === 0) return 'pay_amount';
    return isAuthorisation ? 'pay_flow.pay_and_authorise' : 'pay_flow.pay_and_hold';
  }
  return isAuthorisation ? 'pay_flow.authorise_amount' : 'pay_flow.hold_amount';
}

function getPayAction(t: TFunction, totals: PayActionTotals) {
  const {chargeTotal, heldTotal, currencySymbol} = totals;
  const amountLabel = formatMoney(
    chargeTotal > 0 ? chargeTotal : heldTotal,
    currencySymbol,
  );

  return {
    amountLabel,
    label: t(getPayActionLabelKey(totals), {amount: amountLabel}),
  };
}

export {
  findDepositPayment,
  getCartItemEditLabel,
  getHoldSubtitle,
  getPayAction,
  getDepositFee,
  isProtectionPayment,
  isRetentionPayment,
  isRequestHold,
  isHoldPayment,
  getAmountOnCard,
  splitCart,
  getHoldSummary,
  getInfoHint,
};
export type {CartSplit};
